import { useQueries, useQuery } from '@tanstack/react-query';
import { Plus, QrCode } from 'lucide-react';
import { Link } from 'react-router';
import { api, unwrap } from '../api/client';
import type { AssetStatus } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { AssetLink } from '../components/assets/AssetLink';
import { ButtonLink, Card, CardHeader, EmptyState, ErrorBox, Loading, PageHeader, StatusBadge, Table, Td, Th, Tr } from '../components/ui';
import { formatDate, formatDateTime, STATUS_LABELS } from '../lib/format';

const TILES: Array<{ status: AssetStatus; hint: string }> = [
  { status: 'active', hint: 'im Einsatz' },
  { status: 'in_stock', hint: 'verfügbar' },
  { status: 'repair', hint: 'beim Service' },
  { status: 'lost', hint: 'vermisst' },
];

export function DashboardPage() {
  const { me, can } = useAuth();

  const counts = useQueries({
    queries: TILES.map((t) => ({
      queryKey: ['assets', 'count', t.status],
      queryFn: () => unwrap(api.GET('/api/assets', { params: { query: { status: t.status, page_size: 1 } } })),
    })),
  });
  const unassigned = useQuery({
    queryKey: ['assets', 'count', 'active-unassigned'],
    queryFn: () => unwrap(api.GET('/api/assets', { params: { query: { status: 'active', unassigned: 'true', top_level: 'true', page_size: 1 } } })),
  });
  const recent = useQuery({
    queryKey: ['assets', 'recent'],
    queryFn: () => unwrap(api.GET('/api/assets', { params: { query: { sort: 'updated_at', order: 'desc', page_size: 8 } } })),
  });
  const disposals = useQuery({
    queryKey: ['disposals', 'recent'],
    queryFn: () => unwrap(api.GET('/api/disposals', { params: { query: { page_size: 5 } } })),
  });

  return (
    <>
      <PageHeader
        title={`Hallo ${me.display_name.split(' ')[0]}`}
        subtitle="Überblick über den IT-Bestand"
        actions={
          <>
            <ButtonLink to="/scan" icon={<QrCode className="size-4" />}>
              Scannen
            </ButtonLink>
            {can('asset:write') && (
              <ButtonLink to="/assets/new" variant="primary" icon={<Plus className="size-4" />}>
                Asset anlegen
              </ButtonLink>
            )}
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {TILES.map((t, i) => (
          <Link key={t.status} to={`/assets?status=${t.status}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500">
            <div className="text-xs font-medium text-slate-500">{STATUS_LABELS[t.status]}</div>
            <div className="mt-1 text-2xl font-semibold">{counts[i]?.data?.total ?? '…'}</div>
            <div className="text-xs text-slate-400">{t.hint}</div>
          </Link>
        ))}
        <Link to="/assets?status=active&unassigned=true&top_level=true" className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500">
          <div className="text-xs font-medium text-slate-500">Aktiv ohne Mitarbeiter</div>
          <div className="mt-1 text-2xl font-semibold">{unassigned.data?.total ?? '…'}</div>
          <div className="text-xs text-slate-400">z. B. Server, Drucker</div>
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Zuletzt geändert" actions={<Link to="/assets?sort=updated_at&order=desc" className="text-sm text-brand-700 hover:underline">Alle</Link>} />
          {recent.isPending ? (
            <Loading />
          ) : recent.error ? (
            <ErrorBox error={recent.error} className="m-4" />
          ) : recent.data.data.length === 0 ? (
            <EmptyState title="Noch keine Assets" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Nr.</Th>
                  <Th>Bezeichnung</Th>
                  <Th>Status</Th>
                  <Th>Geändert</Th>
                </tr>
              </thead>
              <tbody>
                {recent.data.data.map((a) => (
                  <Tr key={a.id}>
                    <Td>
                      <AssetLink asset={a} />
                    </Td>
                    <Td>{a.name}</Td>
                    <Td>
                      <StatusBadge status={a.status} />
                    </Td>
                    <Td className="whitespace-nowrap text-slate-500">{formatDateTime(a.updated_at)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Letzte Verschrottungen" actions={<Link to="/disposals" className="text-sm text-brand-700 hover:underline">Alle</Link>} />
          {disposals.isPending ? (
            <Loading />
          ) : disposals.error ? (
            <ErrorBox error={disposals.error} className="m-4" />
          ) : disposals.data.data.length === 0 ? (
            <EmptyState title="Keine Verschrottungen" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {disposals.data.data.map((d) => (
                <li key={d.id} className="px-4 py-2.5 text-sm">
                  <Link to={`/disposals/${d.disposal_number}`} className="font-mono font-medium text-brand-700 hover:underline">
                    {d.disposal_number}
                  </Link>
                  <span className="ml-2 text-slate-500">{formatDate(d.disposal_date)}</span>
                  <div className="truncate text-slate-600">{d.primary_assets.map((a) => `${a.asset_number} ${a.name}`).join(', ')}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
